/**
 * BattleStage — the "live broadcast"UFC-style face-off shell used by
 * Cypher.jsx, PvpDetail.jsx (completed) and Battle.jsx.
 *
 * Layout (reference: UFC boxing broadcast):
 * - Fullscreen arena background image
 * - Two full-body rappers standing bottom-left & bottom-right, facing center
 * - Center overlay: ROUND count top, verse-text bubble middle, name plates
 * bottom (KIDD CANNON · THROW THE TOWEL · G-FLOW style)
 * - Sides: timer / purse / extra-round HUD panels
 * - Judge's Comment panel top-right when the judge is deliberating
 *
 * Full-body character images are generated on a solid-black background so we
 * blend them with `mix-blend-mode: screen` (black → transparent) — this
 * dodges the ugly rectangular headshot frames that made the old UI feel like
 * static portrait cards instead of two rappers standing on the same stage.
 *
 * When a rapper is `rapping` and a `lipsync` MP4 URL is present, we swap the
 * <img> for a <video> so their mouth animates to their verse.
 */
import React, { useEffect, useRef } from "react";
import { apiPortraitUrl } from "@/auth";

const BACKEND = process.env.REACT_APP_BACKEND_URL;

function absAsset(path) {
 if (!path) return null;
 if (/^https?:\/\//.test(path)) return path;
 return path.startsWith("/api/") ? `${BACKEND}${path}` : path;
}

/* ----------------------- Single fighter figure ------------------------- */
const REACTION_TAUNTS = [
 "PFFT!", "NAH BRO", "COME AGAIN?", "I HEAR YOU", "HERE WE GO!",
 "OHHH!", "CORNY", "WEAK", "YEAH?", "TRY AGAIN", "GARBAGE",
 "PSSH", "AND WHAT?", "SIT DOWN", "MID", "PLEASE",
];

function Fighter({ side, name, portrait, motion, lipsync, rapping, muted, onEnded, isWinner, opponentRapping }) {
 // User feedback (Feb 2026): Runway motion loops and wav2lip lipsync videos
 // both ship with black backgrounds that leave visible boxes around the
 // character no matter what CSS blend mode we throw at them. The lipsync
 // was ALSO firing late (during judge verdict phase instead of during the
 // rap), further breaking immersion.
 //
 // FIX: Kill both video paths on the live stage. Use the ALPHA-CHANNEL PNG
 // portraits (which are full-body, transparent-background, look like they
 // ACTUALLY stand on the pitch) as the ONLY source of truth. Give the
 // active rapper rich CSS-driven rap animation (torso lean, head bob, arm
 // pulse) so they move without the box problem. Listener stays perfectly
 // still. This mirrors what the user liked before Runway was introduced,
 // minus the "bouncing picture" — the new keyframes are subtle and
 // realistic. Runway MP4s stay in DB for future highlight-reel export.
 //
 // (Silence unused-var warnings for the props we no longer render — keep
 // them in the signature so call sites don't need to change.)
 void motion; void lipsync; void muted; void onEnded;

 const flip = side === "right" ? "scale-x-[-1]" : "";

 // ESPN-style live reaction: when opponent is rapping, this fighter throws
 // a taunt bubble every 5-9 seconds. Feels like real-time trash-talk between
 // AI rappers instead of two statues waiting their turn.
 const [taunt, setTaunt] = React.useState(null);
 useEffect(() => {
 if (!opponentRapping || rapping) { setTaunt(null); return; }
 let cancelled = false;
 const throwTaunt = () => {
 if (cancelled) return;
 const t = REACTION_TAUNTS[Math.floor(Math.random() * REACTION_TAUNTS.length)];
 setTaunt({ text: t, id: Date.now() });
 setTimeout(() => !cancelled && setTaunt(null), 2200);
 };
 const first = setTimeout(throwTaunt, 3000 + Math.random() * 2000);
 const it = setInterval(throwTaunt, 6000 + Math.random() * 3000);
 return () => { cancelled = true; clearTimeout(first); clearInterval(it); };
 }, [opponentRapping, rapping]);

 // Only render the static PNG. No motion loops, no lipsync videos on stage.
 const showVideo = false;
 const showMotion = false;
 // Cinematic sizing — fighters take up 44% of frame width (was 32%), positioned
 // so their FEET plant on the visible stage floor at ~62% viewport height.
 // The overall container extends from y=8% to y=88% so we see head-to-toe with
 // a spotlight halo grounding them on the stage.
 const posClass = side === "left"
 ? "left-[2%] md:left-[6%]"
 : "right-[2%] md:right-[6%]";
 // Listener stays PERFECTLY STILL (real rap battle: one spits, the other
 // listens). Active rapper gets the full rap-bop + subtle skew lean so the
 // PNG portrait looks like they're MC'ing on stage.
 const bodyAnim = rapping ? "animate-rap-bop" : "animate-idle-sway";

 // Active rapper zooms slightly in + gets rim-light behind. Non-active
 // gets a subtle desaturation so the audience's eye follows the mic.
 const scaleStyle = rapping ? "scale-[1.08]" : "scale-100";
 const filterStyle = rapping ? "" : "brightness-[0.82] saturate-75";

 return (
 <div
 className={`absolute ${posClass} h-[88%] top-[6%] w-[46%] md:w-[42%] flex flex-col items-center justify-end pointer-events-none z-10 transition-transform duration-700 ${scaleStyle}`}
 >
 {/* Rim-light spotlight behind active rapper — huge radial glow that
 simulates stage-key lighting. Makes them pop off the crowd. */}
 {rapping && (
 <div className="absolute inset-0 pointer-events-none"
 style={{ background: `radial-gradient(ellipse at 50% 45%,
 rgba(255,215,0,0.35) 0%,
 rgba(255,215,0,0.12) 35%,
 rgba(0,0,0,0) 70%)` }} />
 )}

 {/* Body wrapper — carries the bop/nod animation (only when we're falling
 back to the static PNG; when a Runway motion loop or wav2lip clip is
 playing, the character already MOVES so we skip the CSS bop). */}
 <div className={`relative w-full flex-1 ${bodyAnim} ${filterStyle} transition-all duration-500`}>
 {portrait ? (
 <img
 src={apiPortraitUrl(portrait) || absAsset(portrait)}
 alt={name}
 className={`absolute inset-0 w-full h-full object-contain ${flip}`}
 style={{
 objectPosition: "center bottom",
 filter: "drop-shadow(0 30px 22px rgba(0,0,0,0.85))",
 }}
 />
 ) : (
 <div className="w-full h-full flex items-center justify-center bg-yt-surface font-display text-2xl text-yt-yellow">
 {(name || "??").slice(0, 2).toUpperCase()}
 </div>
 )}

 {rapping && (
 <>
 {/* Stage-key spotlight halo directly under active rapper's feet */}
 <div className="absolute bottom-[-14px] left-1/2 -translate-x-1/2 w-[85%] h-14 rounded-full bg-yt-yellow/60 blur-3xl pointer-events-none" />
 {/* Second inner spotlight for punch */}
 <div className="absolute bottom-[-6px] left-1/2 -translate-x-1/2 w-[55%] h-6 rounded-full bg-yt-yellow/80 blur-lg pointer-events-none" />
 </>
 )}

 {/* Subtle floor-contact shadow for BOTH fighters (not just active),
 so nobody looks like they're floating. */}
 {!rapping && (
 <div className="absolute bottom-[-4px] left-1/2 -translate-x-1/2 w-[65%] h-3 rounded-full bg-black/55 blur-md pointer-events-none" />
 )}

 {/* Live reaction taunt bubble — appears above the LISTENER while
 the OPPONENT is rapping. ESPN halftime crosstalk vibe. */}
 {taunt && (
 <div key={taunt.id}
 className={`absolute top-[8%] ${side === "left" ? "right-[-2%]" : "left-[-2%]"} pointer-events-none z-20`}>
 <div className="relative bg-yt-yellow text-black font-display text-lg md:text-2xl tracking-tight px-4 py-2 shadow-brutal animate-taunt-pop rotate-[-4deg]">
 {taunt.text}
 <div className={`absolute -bottom-2 ${side === "left" ? "left-4" : "right-4"} w-0 h-0
 border-l-8 border-r-8 border-t-8 border-l-transparent border-r-transparent border-t-yt-yellow`} />
 </div>
 </div>
 )}

 {isWinner && !rapping && (
 <div className="absolute top-2 left-1/2 -translate-x-1/2 bg-yt-yellow text-black text-xs px-2 py-1 font-mono tracking-widest shadow-brutal">
 ★ WIN
 </div>
 )}
 </div>
 </div>
 );
}

/* ----------------------- Broadcast HUD ---------------------------------- */
function HudTimer({ side, label, value }) {
 const pos = side === "left" ? "left-4 md:left-8" : "right-4 md:right-8";
 return (
 <div className={`absolute top-3 md:top-6 ${pos} z-30 bg-black/85 border-2 border-white px-3 py-2 md:px-4 md:py-3 text-center shadow-brutal`}>
 <div className="font-mono text-[9px] md:text-[10px] tracking-[0.3em] text-white/70">{label}</div>
 <div className="font-display text-2xl md:text-4xl text-yt-red leading-none">{value}</div>
 </div>
 );
}

function ThrowTowelBtn({ onClick, disabled, testid = "throw-towel-btn" }) {
 if (!onClick) return null;
 return (
 <button
 data-testid={testid}
 onClick={onClick}
 disabled={disabled}
 className="pointer-events-auto bg-yt-red border-2 border-white text-white font-mono text-xs md:text-sm tracking-[0.25em] px-4 py-2 shadow-brutal hover:bg-white hover:text-yt-red disabled:opacity-40 disabled:cursor-not-allowed"
 >
 THROW THE TOWEL
 </button>
 );
}

/* ----------------------- Main BattleStage ------------------------------ */
export default function BattleStage({
 arenaImage,
 arenaVideo, // optional Sora-2 cinematic B-roll MP4 — overrides arenaImage
 arenaName,
 round,
 totalRounds,
 timerLeft = "1:00",
 timerRight = "1:00",
 centerCue,
 fighterA, // { name, portrait, lipsync, rapping, isWinner }
 fighterB, // { name, portrait, lipsync, rapping, isWinner }
 verseLine,
 judgeComment,
 purseLeft,
 purseRight,
 onThrowTowel, // optional
 onUnmute,
 muted = true,
 extraTopRight, // extra JSX slot (download button etc)
 fullscreen = true, // false → contained (in-flow) mode for Battle.jsx
}) {
 const arenaSrc = arenaImage
 ? (arenaImage.startsWith("http") ? arenaImage : arenaImage)
 : null;

 // All arenas are now Nano-Banana-generated empty-stage shots with proper
 // ground-level framing baked in. Use them at natural size — no CSS zoom
 // needed anymore.
 const arenaTransform = { transform: "scale(1.0)", transformOrigin: "50% 50%" };

 const shellClass = fullscreen
 ? "fixed inset-0 z-50 bg-black overflow-hidden select-none"
 : "relative w-full bg-black overflow-hidden select-none";
 // Mobile: give the stage a MUCH taller minimum height so full-body fighters
 // actually have room to stand + faces read + verse bubble doesn't cover
 // them. On desktop the 16:9 area is fine at 640px; on phones we go to
 // ~78vh so it feels like a proper cinematic broadcast frame.
 const shellStyle = fullscreen ? {} : { minHeight: "min(78vh, 720px)" };

 // When ANY fighter is rapping, dim + slightly blur the stadium crowd
 // so the audience's eye locks onto the mic-holder. Adds cinematic depth-of-field.
 const anyRapping = !!(fighterA?.rapping || fighterB?.rapping);

 return (
 <div data-testid="battle-stage" className={shellClass} style={shellStyle}>
 {/* ARENA BACKGROUND — Sora-2 cinematic B-roll when available, else the
 static PNG. Video autoplay is muted+loop so it plays without user
 gesture. Same visual treatment (blur + scale on rap) applied. */}
 {arenaVideo ? (
 <video
 src={arenaVideo}
 poster={arenaSrc || undefined}
 autoPlay muted loop playsInline
 className={`absolute inset-0 w-full h-full object-cover transition-all duration-700
 ${anyRapping ? "opacity-70 blur-[2px] scale-[1.04]" : "opacity-95 scale-100"}`}
 style={arenaTransform}
 />
 ) : arenaSrc && (
 <img
 src={arenaSrc}
 alt={arenaName || "arena"}
 className={`absolute inset-0 w-full h-full object-cover transition-all duration-700
 ${anyRapping ? "opacity-70 blur-[2px] scale-[1.04]" : "opacity-95 scale-100"}`}
 style={arenaTransform}
 draggable={false}
 />
 )}
 {/* Dark vignette so characters pop off the background */}
 <div className={`absolute inset-0 pointer-events-none transition-opacity duration-700
 ${anyRapping
 ? "bg-gradient-to-b from-black/50 via-black/30 to-black/80"
 : "bg-gradient-to-b from-black/30 via-transparent to-black/60"}`} />
 {/* Stage floor haze for grounded feel */}
 <div className="absolute bottom-0 left-0 right-0 h-1/4 bg-gradient-to-t from-black/70 to-transparent pointer-events-none" />

 {/* SMOKE MACHINES — thick fog rolling in from both wings, cranks
 intensity when someone is rapping. Pure CSS radial gradients + blur. */}
 <div className={`absolute inset-x-0 bottom-0 h-1/3 pointer-events-none transition-opacity duration-1000
 ${anyRapping ? "opacity-90" : "opacity-60"}`}>
 <div className="absolute bottom-0 left-0 w-1/2 h-full animate-smoke-left"
 style={{ background: "radial-gradient(ellipse at 20% 100%, rgba(255,255,255,0.35) 0%, rgba(255,255,255,0.12) 40%, transparent 75%)", filter: "blur(30px)" }} />
 <div className="absolute bottom-0 right-0 w-1/2 h-full animate-smoke-right"
 style={{ background: "radial-gradient(ellipse at 80% 100%, rgba(255,255,255,0.35) 0%, rgba(255,255,255,0.12) 40%, transparent 75%)", filter: "blur(30px)" }} />
 </div>

 {/* LASER SWEEPS — two beams scanning across the stage during rap phases.
 The beams tilt from stage lights on both sides toward center. */}
 {anyRapping && (
 <>
 <div className="absolute top-0 left-[-10%] w-[40%] h-full pointer-events-none animate-laser-left mix-blend-screen"
 style={{ background: "linear-gradient(105deg, transparent 45%, rgba(255,60,60,0.55) 50%, transparent 55%)", filter: "blur(8px)" }} />
 <div className="absolute top-0 right-[-10%] w-[40%] h-full pointer-events-none animate-laser-right mix-blend-screen"
 style={{ background: "linear-gradient(-105deg, transparent 45%, rgba(255,215,0,0.6) 50%, transparent 55%)", filter: "blur(8px)" }} />
 </>
 )}

 {/* PYRO BURSTS — subtle upward-shooting sparks at the base of each side */}
 {anyRapping && (
 <>
 <div className="absolute bottom-0 left-[10%] w-16 h-32 pointer-events-none animate-pyro"
 style={{ background: "radial-gradient(circle at 50% 100%, rgba(255,180,0,0.9) 0%, rgba(255,80,0,0.5) 40%, transparent 80%)", filter: "blur(4px)", mixBlendMode: "screen" }} />
 <div className="absolute bottom-0 right-[10%] w-16 h-32 pointer-events-none animate-pyro"
 style={{ background: "radial-gradient(circle at 50% 100%, rgba(255,180,0,0.9) 0%, rgba(255,80,0,0.5) 40%, transparent 80%)", filter: "blur(4px)", mixBlendMode: "screen", animationDelay: "0.3s" }} />
 </>
 )}

 {/* FIGHTERS */}
 {fighterA && <Fighter side="left" {...fighterA} muted={muted} opponentRapping={!!fighterB?.rapping} />}
 {fighterB && <Fighter side="right" {...fighterB} muted={muted} opponentRapping={!!fighterA?.rapping} />}

 {/* ESPN-STYLE LIVE BROADCAST TICKER — bottom of stage, always visible */}
 <div className="absolute bottom-0 left-0 right-0 h-8 bg-black/85 border-t border-yt-red flex items-center overflow-hidden pointer-events-none z-30">
 <div className="bg-yt-red text-white font-mono text-[10px] tracking-widest px-3 py-1.5 mr-3 whitespace-nowrap">● LIVE</div>
 <div className="flex-1 overflow-hidden">
 <div className="animate-ticker whitespace-nowrap font-mono text-xs text-white tracking-wider">
 {`YOUNGTONGUES.COM · ${arenaName?.toUpperCase() || "THE PITCH"} · ${fighterA?.name?.toUpperCase() || ""} VS ${fighterB?.name?.toUpperCase() || ""} · JUDGE DEGAGNE PRESIDING · ROUND ${round || 1} · ${centerCue || "LIVE"} · SPIT · DROP · CROWN · `.repeat(3)}
 </div>
 </div>
 </div>

 {/* TOP ROW — round label + timers */}
 <div className="absolute top-3 md:top-6 left-1/2 -translate-x-1/2 z-30 text-center">
 <div className="font-mono text-[10px] md:text-xs text-white tracking-[0.4em]">✕ ✕ ROUND {round || 1}{totalRounds ? ` / ${totalRounds}` : ""} ✕ ✕</div>
 {centerCue && (
 <div className="mt-2 bg-yt-red border-2 border-white px-3 py-1 md:px-4 md:py-2 inline-block shadow-brutal">
 <div className="font-display text-lg md:text-2xl text-white leading-none">{centerCue}</div>
 </div>
 )}
 </div>
 <HudTimer side="left" label="TIMER" value={timerLeft} />
 <HudTimer side="right" label="TIMER" value={timerRight} />

 {/* KARAOKE CAPTION BAR — single-line live lyric synced to the audio
 element's currentTime (fed by parent via `verseLine` prop). Sits above
 the bottom name-plate + LIVE ticker so it never blocks the rappers'
 faces. Only renders when a lyric line is present. */}
 {verseLine && (
 <div
 data-testid="battle-stage-karaoke"
 className="absolute left-1/2 -translate-x-1/2 z-30 pointer-events-none w-[92%] md:w-auto"
 style={{ bottom: "clamp(88px, 16vh, 140px)" }}
 >
 <div
 key={verseLine}
 className="bg-black/85 border-2 border-yt-yellow px-4 py-2 md:px-6 md:py-3 shadow-brutal max-w-full md:max-w-3xl text-center animate-taunt-pop"
 >
 <div className="font-display text-base md:text-2xl text-white leading-tight tracking-wide line-clamp-2 md:line-clamp-1">
 &ldquo;{verseLine}&rdquo;
 </div>
 </div>
 </div>
 )}

 {/* JUDGE COMMENT — REMOVED from the stage overlay. Was floating over the
 right rapper's chest/face at top-[42%] right-3, blocking the view.
 Judge commentary is played via <audio> on the below-the-stage panel. */}

 {/* PURSE / EXTRA ROUND badges — pinned above the bottom name-plate row.
 Kept out of the mobile verse-bubble zone (verse is bottom-[18%]). */}
 {purseLeft && (
 <div className="absolute bottom-[calc(88px+8px)] md:bottom-[22%] left-3 md:left-8 z-20 bg-white text-black font-mono text-[10px] md:text-sm px-2 py-1 md:px-3 border-2 border-black shadow-brutal max-w-[65%] md:max-w-none truncate">
 {purseLeft}
 </div>
 )}
 {purseRight && (
 <div className="absolute bottom-[calc(88px+8px)] md:bottom-[22%] right-3 md:right-8 z-20 bg-white text-black font-mono text-[10px] md:text-sm px-2 py-1 md:px-3 border-2 border-black shadow-brutal">
 {purseRight}
 </div>
 )}

 {/* BOTTOM BAR — name plates + throw the towel */}
 <div className="absolute bottom-0 left-0 right-0 z-30 bg-black/95 border-t-2 border-white pointer-events-none">
 <div className="grid grid-cols-[2fr_1fr_2fr] items-center px-4 md:px-8 py-3 md:py-4 gap-3">
 {/* Left name plate + LIVE badge */}
 <div className="flex items-center gap-3 pointer-events-auto min-w-0">
 <div className="bg-yt-red text-white font-mono text-[9px] md:text-xs px-2 py-1 tracking-widest flex-shrink-0">● LIVE</div>
 <div className="font-display text-base md:text-xl lg:text-2xl text-white leading-tight tracking-wide truncate">
 {fighterA?.name?.toUpperCase()}
 </div>
 </div>
 {/* Center — throw the towel */}
 <div className="text-center">
 <ThrowTowelBtn onClick={onThrowTowel} disabled={false} />
 </div>
 {/* Right name plate */}
 <div className="flex items-center gap-3 justify-end pointer-events-auto min-w-0">
 <div className="font-display text-base md:text-xl lg:text-2xl text-white leading-tight tracking-wide truncate text-right">
 {fighterB?.name?.toUpperCase()}
 </div>
 <div className="bg-yt-red text-white font-mono text-[9px] md:text-xs px-2 py-1 tracking-widest flex-shrink-0">LIVE ●</div>
 </div>
 </div>
 </div>

 {/* TOP-RIGHT SLOT — unmute + download + close etc */}
 <div className="absolute top-20 right-3 md:top-32 md:right-6 z-40 flex flex-col gap-2 items-end pointer-events-auto">
 {muted && onUnmute && (
 <button
 data-testid="battle-stage-unmute"
 onClick={onUnmute}
 className="bg-yt-yellow text-black font-mono text-xs tracking-widest px-3 py-2 shadow-brutal hover:bg-white"
 >
 UNMUTE
 </button>
 )}
 {extraTopRight}
 </div>
 </div>
 );
}
